import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import EscrowPaymentExplainer from "@/components/EscrowPaymentExplainer";
import { cn } from "@/lib/utils";

function getPublishableKey() {
  return (
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    process.env.REACT_APP_STRIPE_PUBLIC_KEY ||
    process.env.REACT_APP_STRIPE_PUBLISHABLE ||
    ""
  );
}

function clearStripeMountNode(node) {
  if (!node) return;
  try {
    node.replaceChildren();
  } catch {
    node.innerHTML = "";
  }
}

function cardLabel(pm) {
  const brand = pm?.brand ? String(pm.brand).toUpperCase() : "CARTE";
  const last4 = pm?.last4 ? `•••• ${pm.last4}` : "";
  const exp =
    pm?.exp_month && pm?.exp_year ? ` — ${pm.exp_month}/${String(pm.exp_year).slice(-2)}` : "";
  return `${brand} ${last4}${exp}`.trim();
}

function normalizePaymentMethodsList(body) {
  const b = body && typeof body === "object" ? body : {};
  return Array.isArray(b.payment_methods) ? b.payment_methods : [];
}

export default function StripePaymentDialog({
  open,
  onOpenChange,
  clientSecret,
  intentType = "payment",
  transactionId,
  returnUrl,
  title = "Paiement",
  subtitle = "En séquestre pendant l’appel · débit uniquement si RDV confirmé · remboursement si refus justifié.",
  showEscrowInfo = true,
  confirmLabel = "Payer",
  onSuccess,
  onClientSecretChange,
  /** Cartes déjà chargées en parallèle de /buy — évite d’afficher le formulaire carte avant la liste. */
  initialPaymentMethods = null,
}) {
  const stripePromise = useMemo(() => {
    const key = getPublishableKey();
    return key ? loadStripe(key, { locale: "fr" }) : null;
  }, []);

  const containerRef = useRef(null);
  const stripeMountHostRef = useRef(null);
  const elementsRef = useRef(null);
  const paymentElRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mounting, setMounting] = useState(false);
  const [ready, setReady] = useState(false);
  const [mountGeneration, setMountGeneration] = useState(0);
  const [resettingPayment, setResettingPayment] = useState(false);

  const [savedCards, setSavedCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [cardsLoadError, setCardsLoadError] = useState("");
  const [selectedPmId, setSelectedPmId] = useState("");
  /** true = formulaire Stripe (nouvelle carte) ; false = carte enregistrée choisie dans la liste. */
  const [useCardForm, setUseCardForm] = useState(false);

  const canPickSavedCard = intentType === "payment";

  const applySavedCardsList = useCallback((list) => {
    const cards = Array.isArray(list) ? list : [];
    setSavedCards(cards);
    const def = cards.find((p) => p.is_default) || cards[0];
    if (def?.id) setSelectedPmId(String(def.id));
    else setSelectedPmId("");
    setUseCardForm(cards.length === 0);
    if (cards.length > 0) setError("");
  }, []);

  const hasStripeIframe = useCallback(() => {
    const el = containerRef.current;
    if (!el) return false;
    return Boolean(el.querySelector("iframe"));
  }, []);

  const unmountStripeElement = useCallback(() => {
    try {
      if (paymentElRef.current) paymentElRef.current.unmount();
    } catch {}
    paymentElRef.current = null;
    elementsRef.current = null;
    clearStripeMountNode(stripeMountHostRef.current);
    stripeMountHostRef.current = null;
    setReady(false);
  }, []);

  const mountStripeElement = useCallback(async (generation) => {
    if (!clientSecret || !stripePromise || !useCardForm) return;
    setMounting(true);
    setError("");
    setReady(false);

    try {
      const startWait = Date.now();
      while (!containerRef.current && Date.now() - startWait < 2500) {
        await new Promise((r) => setTimeout(r, 50));
      }
      if (generation !== mountGeneration) return;
      if (!containerRef.current) {
        throw new Error("Formulaire de paiement indisponible (réessayez).");
      }

      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe indisponible (script bloqué ou réseau).");
      if (generation !== mountGeneration) return;

      unmountStripeElement();

      const mountHost = containerRef.current;
      stripeMountHostRef.current = mountHost;

      elementsRef.current = stripe.elements({ clientSecret });
      paymentElRef.current = elementsRef.current.create("payment", {
        layout: "tabs",
        fields: { billingDetails: "auto" },
        wallets: { link: "never", applePay: "never", googlePay: "never" },
      });

      const el = paymentElRef.current;
      el.on("ready", () => {
        if (generation === mountGeneration) {
          setReady(true);
          setError("");
        }
      });
      el.on("loaderror", (event) => {
        if (generation !== mountGeneration) return;
        const msg = event?.error?.message || "Impossible de charger le formulaire Stripe.";
        setError(msg);
      });

      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      if (generation !== mountGeneration) return;

      el.mount(mountHost);
    } catch (e) {
      console.error("[stripe] init payment element:", e);
      if (generation === mountGeneration) {
        setError(e?.message || "Impossible d'initialiser Stripe");
      }
    } finally {
      if (generation === mountGeneration) setMounting(false);
    }
  }, [clientSecret, mountGeneration, stripePromise, useCardForm, unmountStripeElement]);

  useEffect(() => {
    if (!open || !canPickSavedCard) {
      setSavedCards([]);
      setSelectedPmId("");
      setUseCardForm(!canPickSavedCard);
      setCardsLoading(false);
      return undefined;
    }

    const hasPrefetch = Array.isArray(initialPaymentMethods);
    if (hasPrefetch) {
      applySavedCardsList(initialPaymentMethods);
      setCardsLoading(false);
      setCardsLoadError("");
    } else {
      setCardsLoading(true);
      setUseCardForm(false);
    }

    let cancelled = false;
    if (!hasPrefetch) setCardsLoadError("");

    (async () => {
      try {
        const res = await api.get("/payment-methods/list", {
          params: {
            light: 1,
            ...(transactionId ? { transaction_id: String(transactionId) } : {}),
          },
        });
        if (cancelled) return;
        applySavedCardsList(normalizePaymentMethodsList(res?.data));
      } catch (e) {
        if (!cancelled) {
          if (!hasPrefetch) {
            setSavedCards([]);
            setUseCardForm(true);
          }
          const d = e?.response?.data?.detail;
          setCardsLoadError(typeof d === "string" ? d : "Impossible de charger vos cartes enregistrées.");
        }
      } finally {
        if (!cancelled) setCardsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, canPickSavedCard, transactionId, initialPaymentMethods, applySavedCardsList]);

  useEffect(() => {
    if (!open || !clientSecret || !stripePromise || !useCardForm || (canPickSavedCard && cardsLoading)) {
      if (!useCardForm) unmountStripeElement();
      if (!stripePromise && open && clientSecret) {
        setError("Clé Stripe publishable manquante (REACT_APP_STRIPE_PUBLISHABLE_KEY).");
      }
      return undefined;
    }

    const generation = mountGeneration;
    let errorTid = null;
    let iframePollTid = null;

    void mountStripeElement(generation);

    iframePollTid = window.setInterval(() => {
      if (generation !== mountGeneration) return;
      if (hasStripeIframe()) {
        setReady(true);
        setError((prev) => (prev?.includes("Stripe ne se charge pas") ? "" : prev));
      }
    }, 400);

    errorTid = window.setTimeout(() => {
      if (generation !== mountGeneration) return;
      if (!hasStripeIframe()) {
        setError(
          (prev) =>
            prev ||
            "Le formulaire carte ne s’affiche pas. Utilisez « Saisir une nouvelle carte » ou « Réessayer ».",
        );
      }
    }, 14000);

    return () => {
      window.clearTimeout(errorTid);
      window.clearInterval(iframePollTid);
      unmountStripeElement();
    };
  }, [
    open,
    clientSecret,
    stripePromise,
    mountGeneration,
    mountStripeElement,
    hasStripeIframe,
    useCardForm,
    unmountStripeElement,
    canPickSavedCard,
    cardsLoading,
  ]);

  useEffect(() => {
    if (!open) {
      setReady(false);
      setError("");
      setResettingPayment(false);
      setSavedCards([]);
      setCardsLoadError("");
      setSelectedPmId("");
      setUseCardForm(true);
      unmountStripeElement();
    }
  }, [open, unmountStripeElement]);

  const remountPaymentForm = useCallback(() => {
    setMountGeneration((g) => g + 1);
  }, []);

  const showSavedCardPicker = canPickSavedCard && savedCards.length > 0 && !useCardForm && !cardsLoading;
  const showCardEntry = useCardForm && !cardsLoading;

  const switchToCardForm = useCallback(() => {
    setError("");
    setUseCardForm(true);
    remountPaymentForm();
  }, [remountPaymentForm]);

  const switchToSavedCards = useCallback(() => {
    setError("");
    unmountStripeElement();
    setUseCardForm(false);
    if (!selectedPmId && savedCards[0]?.id) {
      setSelectedPmId(String(savedCards[0].id));
    }
  }, [unmountStripeElement, selectedPmId, savedCards]);

  const handleResetPayment = useCallback(async () => {
    setError("");
    setReady(false);
    if (intentType !== "payment" || !transactionId) {
      switchToCardForm();
      return;
    }
    setResettingPayment(true);
    try {
      const res = await api.post("/payments/reset-intent", { transaction_id: String(transactionId) });
      const cs = res?.data?.clientSecret || res?.data?.client_secret;
      if (!cs) throw new Error("Réponse serveur incomplète");
      onClientSecretChange?.(String(cs));
      switchToCardForm();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === "string" ? d : e?.message || "Impossible de réinitialiser le paiement");
      switchToCardForm();
    } finally {
      setResettingPayment(false);
    }
  }, [intentType, transactionId, onClientSecretChange, switchToCardForm]);

  const canConfirm = useMemo(() => {
    if (!clientSecret || loading || resettingPayment) return false;
    if (showSavedCardPicker) return Boolean(selectedPmId);
    return ready && !mounting;
  }, [clientSecret, loading, resettingPayment, showSavedCardPicker, selectedPmId, ready, mounting]);

  const confirm = async () => {
    setError("");
    if (!stripePromise) {
      setError("Stripe indisponible.");
      return;
    }
    setLoading(true);
    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe indisponible");

      if (showSavedCardPicker && selectedPmId) {
        const { error: err, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
          payment_method: selectedPmId,
        });
        if (err) {
          const msg = String(err.message || "");
          const pmCustomerMismatch =
            /does not belong to the Customer/i.test(msg) ||
            /n’appartient pas au client/i.test(msg);
          if (pmCustomerMismatch) {
            setError(
              "Cette carte n’est pas associée à ce paiement. Choisissez « Saisir une autre carte » ou réessayez.",
            );
            setUseCardForm(true);
            remountPaymentForm();
            return;
          }
          try {
            if (transactionId) {
              await api.post("/payments/cancel", { transaction_id: String(transactionId) });
            }
          } catch {}
          setError(msg || "Paiement refusé — essayez une autre carte ou saisissez-en une nouvelle.");
          return;
        }
        const piDone =
          paymentIntent &&
          (paymentIntent.status === "succeeded" ||
            paymentIntent.status === "processing" ||
            paymentIntent.status === "requires_capture");
        if (piDone) {
          try {
            if (transactionId && paymentIntent.status === "requires_capture") {
              await api.post("/payments/confirm", { transaction_id: String(transactionId) });
            }
            await onSuccess?.(paymentIntent);
          } catch (syncErr) {
            console.error("[stripe] payments/confirm:", syncErr);
            setError(
              syncErr?.message ||
                "Paiement accepté par Stripe mais synchronisation impossible. Réessayez depuis « Mes achats ».",
            );
          }
        }
        return;
      }

      if (!elementsRef.current) {
        setError("Formulaire carte non prêt — patientez ou choisissez une carte enregistrée.");
        return;
      }

      if (intentType === "setup") {
        const { error: err, setupIntent } = await stripe.confirmSetup({
          elements: elementsRef.current,
          confirmParams: { return_url: returnUrl || window.location.href },
          redirect: "if_required",
        });
        if (err) setError(err.message || "Paiement refusé");
        if (!err && setupIntent && (setupIntent.status === "succeeded" || setupIntent.status === "processing")) {
          try {
            await onSuccess?.(setupIntent);
          } catch {}
        }
      } else {
        const { error: err, paymentIntent } = await stripe.confirmPayment({
          elements: elementsRef.current,
          confirmParams: { return_url: returnUrl || window.location.href },
          redirect: "if_required",
        });
        if (err) {
          try {
            if (transactionId) {
              await api.post("/payments/cancel", { transaction_id: String(transactionId) });
            }
          } catch {}
          setError(err.message || "Paiement refusé");
        }
        const piDone =
          paymentIntent &&
          (paymentIntent.status === "succeeded" ||
            paymentIntent.status === "processing" ||
            paymentIntent.status === "requires_capture");
        if (!err && piDone) {
          try {
            if (transactionId && paymentIntent.status === "requires_capture") {
              await api.post("/payments/confirm", { transaction_id: String(transactionId) });
            }
            await onSuccess?.(paymentIntent);
          } catch (syncErr) {
            console.error("[stripe] payments/confirm:", syncErr);
            setError(
              syncErr?.message ||
                "Paiement accepté par Stripe mais synchronisation impossible. Réessayez depuis « Mes achats ».",
            );
          }
        }
      }
    } catch (e) {
      console.error("[stripe] confirmPayment:", e);
      setError(e?.message || "Erreur paiement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal>
      <DialogContent
        className="flex max-h-[85vh] w-[calc(100vw-1.5rem)] max-w-lg flex-col overflow-hidden p-0 shadow-lg sm:rounded-2xl"
        data-testid="stripe-payment-dialog"
        onInteractOutside={(e) => {
          const t = e.target;
          if (t instanceof Element && (t.closest("iframe") || t.closest("[class*='Stripe']"))) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="border-b border-slate-100 bg-white px-6 py-4 sm:px-6">
          <DialogTitle className="font-display text-lg font-semibold leading-snug sm:text-xl">{title}</DialogTitle>
          {subtitle ? (
            <div className="mt-2 text-sm leading-relaxed text-slate-600">{subtitle}</div>
          ) : null}
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-white px-6 py-4 sm:px-6">
          {showEscrowInfo ? <EscrowPaymentExplainer /> : null}

          {!clientSecret ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
              Paiement indisponible : <span className="font-semibold">clientSecret manquant</span>.
            </div>
          ) : null}

          <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
            <div className="mb-3 text-sm font-semibold text-slate-900">Moyen de paiement</div>

            {error ? (
              <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">{error}</div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {savedCards.length > 0 ? (
                      <button
                        type="button"
                        className="rounded-md border border-red-200 bg-white px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50"
                        onClick={switchToSavedCards}
                      >
                        Mes cartes
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="rounded-md border border-red-200 bg-white px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50"
                      onClick={switchToCardForm}
                      disabled={resettingPayment}
                    >
                      Nouvelle carte
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {cardsLoading && canPickSavedCard ? (
              <div className="mb-3 space-y-2 animate-pulse" data-testid="stripe-cards-loading">
                <div className="h-12 rounded-lg bg-slate-100" />
                <div className="h-12 rounded-lg bg-slate-100" />
                <p className="text-xs text-slate-500">Chargement de vos cartes…</p>
              </div>
            ) : null}
            {cardsLoadError ? (
              <p className="mb-3 text-xs text-amber-800">{cardsLoadError} Vous pouvez saisir une carte ci-dessous.</p>
            ) : null}

            {!cardsLoading && savedCards.length === 0 && !cardsLoadError && canPickSavedCard ? (
              <p className="mb-3 rounded-lg border border-dashed border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
                Aucune carte enregistrée pour le moment. Saisissez une carte ci-dessous — elle sera mémorisée pour
                vos prochains achats.
              </p>
            ) : null}

            {showSavedCardPicker ? (
              <div className="space-y-3" data-testid="stripe-saved-cards-picker">
                <p className="text-xs text-slate-600">
                  Choisissez la carte à débiter pour ce dossier (séquestre Stripe).
                </p>
                <ul className="space-y-2" role="radiogroup" aria-label="Carte pour ce paiement">
                  {savedCards.map((pm) => (
                    <li key={pm.id}>
                      <label
                        className={cn(
                          "flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-3 text-sm transition-colors",
                          selectedPmId === pm.id
                            ? "border-[#2D7FF9] bg-white ring-2 ring-[#2D7FF9]/25"
                            : "border-slate-200 bg-white hover:border-slate-300",
                        )}
                      >
                        <input
                          type="radio"
                          name="stripe-dialog-pm"
                          className="h-4 w-4 shrink-0 accent-[#2D7FF9]"
                          checked={selectedPmId === pm.id}
                          onChange={() => setSelectedPmId(String(pm.id))}
                          data-testid={`stripe-pm-${pm.id}`}
                        />
                        <span className="min-w-0 flex-1 font-medium text-slate-800">{cardLabel(pm)}</span>
                        {pm.is_default ? (
                          <span className="shrink-0 text-[10px] font-semibold uppercase text-slate-500">
                            Par défaut
                          </span>
                        ) : null}
                      </label>
                    </li>
                  ))}
                </ul>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 w-full rounded-lg border-slate-200 text-slate-700"
                  onClick={switchToCardForm}
                  disabled={resettingPayment}
                  data-testid="stripe-use-new-card"
                >
                  Saisir une autre carte
                </Button>
              </div>
            ) : showCardEntry ? (
              <>
                {savedCards.length > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mb-3 h-9 rounded-lg border-slate-200 text-slate-700"
                    onClick={switchToSavedCards}
                    disabled={resettingPayment || mounting}
                    data-testid="stripe-back-to-saved-cards"
                  >
                    ← Choisir une carte enregistrée
                  </Button>
                ) : null}
                <div className="relative">
                  {mounting && !ready ? (
                    <div className="pointer-events-none absolute inset-0 z-0 min-h-[220px] animate-pulse space-y-3">
                      <div className="h-10 rounded-xl bg-slate-100" />
                      <div className="grid grid-cols-2 gap-3">
                        <div className="h-10 rounded-xl bg-slate-100" />
                        <div className="h-10 rounded-xl bg-slate-100" />
                      </div>
                      <div className="h-10 rounded-xl bg-slate-100" />
                    </div>
                  ) : null}
                  <div ref={containerRef} className="relative z-[1] min-h-[220px]" />
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
                  <span>Saisie sécurisée par Stripe (carte bancaire).</span>
                  {transactionId ? (
                    <button
                      type="button"
                      className="font-semibold text-slate-600 underline-offset-2 hover:underline disabled:opacity-50"
                      disabled={resettingPayment}
                      onClick={() => void handleResetPayment()}
                    >
                      {resettingPayment ? "Réinitialisation…" : "Problème ? Réinitialiser"}
                    </button>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </div>

        <DialogFooter className="flex-col-reverse gap-2 border-t border-slate-100 bg-white px-6 py-4 sm:flex-row sm:justify-end sm:gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="h-11 w-full rounded-lg border-slate-200 sm:w-auto"
          >
            Annuler
          </Button>
          <Button
            className="btn-primary h-11 w-full rounded-lg px-6 font-semibold sm:w-auto"
            onClick={confirm}
            disabled={!canConfirm}
            data-testid="stripe-confirm-pay"
          >
            {loading ? "Paiement..." : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
